import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { parseHistoryChainId } from '@/components/History/utils';

import type { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { tokenKey } from '@/utils/normalizeAddress';

import { tokenEntityQueryOptions } from './tokenEntityCache';

type TradeTokenResults = {
  takerTokenResult: TokenResult | undefined;
  makerTokenResult: TokenResult | undefined;
};

/**
 * Resolves the taker/maker token results for a page of trade history entries.
 * Each unique (address, chainId) pair is its own entity query; the shared token
 * loader coalesces the concurrent fetches into batched `/api/tokens/by-address`
 * requests, avoiding N+1 per-row calls.
 *
 * Returns a `getTradeTokenResults(trade)` resolver instead of a raw map so the
 * taker-on-origin / maker-on-destination keying stays identical between the
 * fetch step and the lookup step.
 */
export function useTradeHistoryTokenResults(trades: MatchaTradeHistoryEntry[]): {
  getTradeTokenResults: (trade: MatchaTradeHistoryEntry) => TradeTokenResults;
} {
  // Collect all unique (address, chainId) pairs across the current page of trades.
  const pairs = useMemo(() => {
    const seen = new Set<string>();
    const out: { address: string; chainId: number; key: string }[] = [];

    const add = (address: string | undefined, chainId: number | undefined) => {
      if (!address || !chainId) return;
      const key = tokenKey(address, chainId);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ address, chainId, key });
    };

    for (const trade of trades) {
      const chainId = parseHistoryChainId(trade.chainId);
      const destinationChainId = parseHistoryChainId(trade.destinationChainId);
      add(trade.takerToken, chainId);
      add(trade.makerToken, destinationChainId ?? chainId);
    }

    return out;
  }, [trades]);

  const queries = useQueries({
    queries: pairs.map(({ address, chainId }) => tokenEntityQueryOptions(address, chainId)),
  });

  const dataSignature = queries
    .map((query) => (query.data ? tokenKey(query.data.address, query.data.chainId) : '-'))
    .join('|');

  const tokenResultsMap = useMemo(() => {
    const resultsMap = new Map<string, TokenResult>();
    pairs.forEach(({ key }, index) => {
      const token = queries[index]?.data;
      if (token) resultsMap.set(key, token);
    });
    return resultsMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs, dataSignature]);

  const getTradeTokenResults = useCallback(
    (trade: MatchaTradeHistoryEntry): TradeTokenResults => {
      const chainId = parseHistoryChainId(trade.chainId);
      const destinationChainId = parseHistoryChainId(trade.destinationChainId);
      const makerChainId = destinationChainId ?? chainId;

      const takerKey = trade.takerToken && chainId ? tokenKey(trade.takerToken, chainId) : '';
      const makerKey =
        trade.makerToken && makerChainId ? tokenKey(trade.makerToken, makerChainId) : '';

      return {
        takerTokenResult: tokenResultsMap.get(takerKey),
        makerTokenResult: tokenResultsMap.get(makerKey),
      };
    },
    [tokenResultsMap],
  );

  return { getTradeTokenResults };
}
